import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { qk } from '@/lib/queryKeys.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, FormField } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { ConfirmDialog } from '@/components/ConfirmDialog.jsx';
import { EmployeePicker } from '@/components/EmployeePicker.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { formatLKR, centsToRupees } from '@/lib/format.js';
import { Plus, Trash2, Play } from 'lucide-react';

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_OPTIONS = MONTHS.slice(1).map((m, i) => ({ value: i + 1, label: m }));
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => {
  const year = new Date().getFullYear() - 5 + i;
  return { value: year, label: year };
});

function statusBadgeVariant(status) {
  return status === 'active' ? 'destructive' : 'secondary';
}

function ReleaseHoldDialog({ hold, onClose }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [notes, setNotes] = useState('');

  const releaseMutation = useMutation({
    mutationFn: () => api.post(`/salary-holds/${hold.id}/release`, { release_notes: notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.salaryHolds() });
      qc.invalidateQueries({ queryKey: qk.salaryHold(hold.id) });
      toast({ variant: 'success', title: 'Salary hold released' });
      onClose();
    },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Release Salary Hold</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">Employee</div>
            <div className="text-base font-semibold">{hold.full_name} ({hold.emp_no})</div>
          </div>
          <div>
            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">Hold Period</div>
            <div className="text-base">
              {MONTHS[hold.start_month]} {hold.start_year}
              {hold.end_month ? ` – ${MONTHS[hold.end_month]} ${hold.end_year}` : ' – Indefinite'}
            </div>
          </div>
          <FormField label="Release Notes">
            <textarea
              className="h-20 w-full px-2 py-1 border border-slate-300 dark:border-slate-700 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Matter resolved — resume from June 2026"
            />
          </FormField>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => releaseMutation.mutate()} disabled={releaseMutation.isPending}>Release Hold</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SalaryHoldsPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [releaseDialogHold, setReleaseDialogHold] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('');
  const now = new Date();
  const [draft, setDraft] = useState({
    employee_id: '',
    start_year: now.getFullYear(),
    start_month: now.getMonth() + 1,
    end_year: '',
    end_month: '',
    reason: '',
  });

  const filters = {};
  if (filterStatus !== 'all') filters.status = filterStatus;
  if (filterEmployee) filters.employee_id = filterEmployee;

  const { data = [], isLoading } = useQuery({
    queryKey: qk.salaryHolds(filters),
    queryFn: () => api.get('/salary-holds', { params: filters }),
  });

  const createMutation = useMutation({
    mutationFn: () => api.post('/salary-holds', {
      employee_id: Number(draft.employee_id),
      start_year: Number(draft.start_year),
      start_month: Number(draft.start_month),
      end_year: draft.end_year ? Number(draft.end_year) : null,
      end_month: draft.end_month ? Number(draft.end_month) : null,
      reason: draft.reason || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.salaryHolds() });
      setOpen(false);
      setDraft({
        employee_id: '',
        start_year: now.getFullYear(),
        start_month: now.getMonth() + 1,
        end_year: '',
        end_month: '',
        reason: '',
      });
      toast({ variant: 'success', title: 'Salary hold created' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.del(`/salary-holds/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.salaryHolds() });
      toast({ variant: 'success', title: 'Salary hold deleted' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  const columns = [
    { key: 'emp_no', header: 'Emp No', className: 'font-mono', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    { 
      key: 'status', 
      header: 'Status', 
      render: (r) => <Badge variant={statusBadgeVariant(r.status)}>{r.status === 'active' ? 'ACTIVE' : 'Released'}</Badge>,
      sortable: true,
    },
    {
      key: 'period',
      header: 'Start Period',
      render: (r) => `${MONTHS[r.start_month]} ${r.start_year}`,
      sortable: false,
    },
    {
      key: 'end_period',
      header: 'End Period',
      render: (r) => r.end_month ? `${MONTHS[r.end_month]} ${r.end_year}` : 'Indefinite',
      sortable: false,
    },
    { key: 'reason', header: 'Reason', sortable: false },
    {
      key: 'accumulated_held_cents',
      header: 'Accumulated Held',
      accessor: (r) => r.accumulated_held_cents || 0,
      render: (r) => <span className="text-right tabular-nums font-mono text-red-600 font-semibold">{formatLKR(r.accumulated_held_cents || 0)}</span>,
      className: 'text-right',
      headClassName: 'text-right',
      sortable: false,
    },
    {
      key: 'released_at',
      header: 'Released At',
      render: (r) => r.released_at ? new Date(r.released_at).toLocaleDateString() : '—',
      sortable: false,
    },
    {
      key: '__a',
      header: '',
      className: 'w-24 text-right',
      render: (r) => (
        <div className="flex justify-end gap-1">
          {r.status === 'active' && (
            <Button
              variant="ghost"
              size="icon"
              title="Release hold"
              onClick={() => setReleaseDialogHold(r)}
            >
              <Play className="h-4 w-4 text-amber-600" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            title="Delete hold"
            onClick={() => setDeleteTarget(r)}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Salary Holds"
        description="Temporarily withhold employee salary while maintaining payroll records"
        actions={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" />New Hold</Button>}
      />

      {/* Filters */}
      <Section className="flex gap-4 items-end">
        <div>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Employee</label>
          <EmployeePicker value={filterEmployee} onChange={setFilterEmployee} />
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Status</label>
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-32">
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="released">Released</option>
            </Select>
          </div>
          {(filterEmployee || filterStatus !== 'all') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilterEmployee('');
                setFilterStatus('all');
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </Section>

      {/* Data table */}
      <Section>
        <DataTable columns={columns} data={data} loading={isLoading} exportFilename="salary-holds.csv" />
      </Section>

      {/* New hold dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Salary Hold</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <FormField label="Employee" required>
              <EmployeePicker value={draft.employee_id} onChange={(v) => setDraft({ ...draft, employee_id: v })} />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Start Month" required>
                <Select
                  value={draft.start_month}
                  onChange={(e) => setDraft({ ...draft, start_month: Number(e.target.value) })}
                >
                  {MONTH_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Start Year" required>
                <Select
                  value={draft.start_year}
                  onChange={(e) => setDraft({ ...draft, start_year: Number(e.target.value) })}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y.value} value={y.value}>{y.label}</option>
                  ))}
                </Select>
              </FormField>
              <FormField label="End Month (Optional)">
                <Select
                  value={draft.end_month}
                  onChange={(e) => setDraft({ ...draft, end_month: e.target.value })}
                >
                  <option value="">No end date (indefinite)</option>
                  {MONTH_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Select>
              </FormField>
              <FormField label="End Year (Optional)">
                <Select
                  value={draft.end_year}
                  onChange={(e) => setDraft({ ...draft, end_year: e.target.value })}
                  disabled={!draft.end_month}
                >
                  <option value="">—</option>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y.value} value={y.value}>{y.label}</option>
                  ))}
                </Select>
              </FormField>
            </div>

            <FormField label="Reason">
              <textarea
                className="h-20 w-full px-2 py-1 border border-slate-300 dark:border-slate-700 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={draft.reason}
                onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                placeholder="e.g., Pending investigation, legal dispute, absconding, etc."
              />
            </FormField>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!draft.employee_id || createMutation.isPending}
            >
              Create Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Release hold dialog */}
      {releaseDialogHold && <ReleaseHoldDialog hold={releaseDialogHold} onClose={() => setReleaseDialogHold(null)} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete Salary Hold?"
        description={`Delete hold for "${deleteTarget?.full_name}" (${deleteTarget?.emp_no})? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        onConfirm={() => {
          deleteMutation.mutate(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
